/**
 * EmployeeFilterHeader — DRY template header for employee-scoped views (training, compliance).
 *
 * Renders the three-tab filter bar (All / My {primitive} / Employee),
 * and the employee dropdown for selecting a specific staff member.
 *
 * Similar to SubjectFilterHeader but for HUMAN actors (employees) instead of
 * SUBJECT actors (residents/patients).
 *
 * ## Usage
 *
 * ```tsx
 * const scope = useEmployeeScope({ nameResolver: resolveActorName });
 *
 * <EmployeeFilterHeader
 *   scope={scope}
 *   actorName={actorName}
 *   actorLoading={actorLoading}
 *   primitiveLabel="Training"    // label for "My Training" tab
 * />
 * ```
 *
 * @see useEmployeeScope — companion hook providing all state and data
 */

import { useMemo } from 'react';
import {
  SegmentedControl,
  Group,
  Text,
  Badge,
  Loader,
  Select,
  Stack,
} from '@mantine/core';
import type { UseEmployeeScopeReturn, EmployeeScopeFilter } from './hooks/useEmployeeScope';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface EmployeeFilterHeaderProps {
  /** Output of useEmployeeScope() — all state and data. */
  readonly scope: UseEmployeeScopeReturn;
  /** Display name of the current user's actor (for "My {primitive}" badge). */
  readonly actorName?: string;
  /** Whether the actor is still loading. */
  readonly actorLoading?: boolean;
  /**
   * Label for the second tab, e.g., "Training", "Compliance", "Tasks".
   * Renders as "My Training", "My Compliance", etc.
   * Defaults to "Items".
   */
  readonly primitiveLabel?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EmployeeFilterHeader({
  scope,
  actorName,
  actorLoading = false,
  primitiveLabel = 'Items',
}: Readonly<EmployeeFilterHeaderProps>) {
  const {
    filter,
    setFilter,
    selectedEmployeeId,
    setSelectedEmployeeId,
    employeeOptions,
    employeesLoading,
    employeeLabel,
    selectedEmployeeName,
  } = scope;

  const selectData = useMemo(
    () => employeeOptions.map((o) => ({ value: o.value, label: o.label })),
    [employeeOptions],
  );

  const segmentedData: { label: string; value: EmployeeScopeFilter }[] = [
    { label: 'All', value: 'all' },
    { label: `My ${primitiveLabel}`, value: 'my' },
    { label: employeeLabel, value: 'employee' },
  ];

  // "My {primitive}" badge content
  let actorBadge: React.ReactNode = null;
  if (actorLoading) {
    actorBadge = <Loader size="xs" />;
  } else if (actorName) {
    actorBadge = <Badge variant="light" color="blue" size="sm">{actorName}</Badge>;
  } else {
    actorBadge = <Text size="xs" c="dimmed">No actor found for current user</Text>;
  }

  return (
    <Stack gap="sm" mb="sm">
      <Group gap="md">
        <SegmentedControl
          size="xs"
          value={filter}
          onChange={(v) => setFilter(v as EmployeeScopeFilter)}
          data={segmentedData}
        />

        {/* "My {primitive}" badge — show current user's name */}
        {filter === 'my' && <Group gap="xs">{actorBadge}</Group>}

        {/* Employee dropdown */}
        {filter === 'employee' && (
          <Select
            size="xs"
            placeholder={
              employeesLoading
                ? `Loading ${employeeLabel.toLowerCase()}s...`
                : `Select ${employeeLabel.toLowerCase()}...`
            }
            data={selectData}
            value={selectedEmployeeId}
            onChange={setSelectedEmployeeId}
            searchable
            clearable
            disabled={employeesLoading}
            nothingFoundMessage={
              employeesLoading ? 'Loading...' : `No ${employeeLabel.toLowerCase()}s found`
            }
            w={220}
          />
        )}

        {/* Selected employee badge */}
        {filter === 'employee' && selectedEmployeeId && (
          <Badge variant="light" color="teal" size="sm">
            {selectedEmployeeName}
          </Badge>
        )}
      </Group>
    </Stack>
  );
}
